import { LMECompanySettings } from "@mcleod/general";
import { AutogenLayoutOrderDuplicateControl } from "./autogen/AutogenLayoutOrderDuplicateControl";
import { DataSourceAction, DataSourceExecutionEvent, EditRowDecorator, LookupModelSelectionEvent, TableRow, TableRowDisplayEvent,  DropdownSelectionEvent, Textbox, TableCell,  TableContentsChangedEvent, TableAction, DataDisplayEvent, Table } from "@mcleod/components";
import { DuplicateControlSlideout } from "./DuplicateControlSlideout";
import { Model, ModelRow } from "@mcleod/core";

export class OrderDuplicateControl extends AutogenLayoutOrderDuplicateControl {
    private _companyIds: Array<string> = [];
    private _companyId: string = LMECompanySettings.get().id;
    private _hasDuplicateAfterOrderEntry: boolean = false;
    private _hasDuplicate: boolean = false;

    override onLoad(): void | Promise<void> {
        this.tableOrderDuplicateControl.onEditLayoutLoaded = (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            rowDecorator.labelTitle.text = `Edit Duplicate Controls`;
            const type = tableRow.data?.get("copy_type");
            const slideout = rowDecorator.layout as DuplicateControlSlideout;
            slideout.companyIds = this._companyIds;
            slideout.hasDuplicateAfterOrderEntry = this._hasDuplicateAfterOrderEntry;
            slideout.hasDuplicate = this._hasDuplicate;
            if (type !== "C") {
                slideout.textboxCopyTo.visible = false;
                slideout.textboxCopyTo.required = false;
                slideout.panelActions.visible = false;
            } else {
                slideout.textboxCopyTo.visible = true;
                slideout.textboxCopyTo.required = true;
                slideout.panelActions.visible = true;
            }

            const copyBillTo = slideout.mainDataSource.activeRow.data["copy_bill_to"];
            const copyCtrlParty = slideout.mainDataSource.activeRow.data["copy_ctrl_party"];
            const copyOvrdPayee = slideout.mainDataSource.activeRow.data["copy_ovrd_payee"];
            const copyRevenueId = slideout.mainDataSource.activeRow.data["copy_revenue_id"];
            const companyId = slideout.textboxCopyTo.dataSource.activeRow.get("copy_to_company");

            if (copyBillTo || copyCtrlParty || copyOvrdPayee || copyRevenueId) {
                Model.search("lme/general/multicompany-copy-control-search", {company_id: companyId, customer_id: copyBillTo, payee_id: copyOvrdPayee, location_id: copyCtrlParty, revenue_code_id: copyRevenueId}).then((row) => {
                    const data = row.getSingleModelRow();
                    
                    const name = data?.get("customer_name");
                    slideout.activeRow.setValueAndLookupModelData("copy_bill_to", copyBillTo, {name: name, id: copyBillTo});
                    slideout.textboxCopyBillTo.displayComponentData(slideout.activeRow, null, 0);

                    const locationName = data?.get("location_name");
                    slideout.activeRow.setValueAndLookupModelData("copy_ctrl_party", copyCtrlParty, {name: locationName, id: copyCtrlParty});
                    slideout.textboxCCtrlParty.displayComponentData(slideout.activeRow, null, 0);

                    const payeeName = data?.get("payee_name");
                    slideout.activeRow.setValueAndLookupModelData("copy_ovrd_payee", copyOvrdPayee, {name: payeeName, id: copyOvrdPayee});
                    slideout.textboxCopyOvrdPayee.displayComponentData(slideout.activeRow, null, 0);

                    const revenueDescr = data?.get("revenue_code_descr");
                    slideout.activeRow.setValueAndLookupModelData("copy_revenue_id", copyRevenueId, {descr: revenueDescr, id: copyRevenueId});
                    slideout.textboxCopyRevenueId.displayComponentData(slideout.activeRow, null, 0);
                });
            }
        };      
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxCopyToCompany. */
    textboxCopyToCompanyBeforeLookupModelSearch(event) {
        // This filters out the current company and any companies that already have a copy control record.
        const filters = [
            `!${LMECompanySettings.get().id}`,
            ...this._companyIds.map(id => `!${id}`)
        ];
        event.filter.id = filters.join(" && ");
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCopyToCompany. */
    textboxCopyToCompanyOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const deselectedId = event.deselection?.get("id")
        const selectedId = event.selection?.get("id")
        
        if(deselectedId) this._companyIds = this._companyIds.filter(id => id !== deselectedId)
        if(selectedId) this._companyIds.push(selectedId)
    }

    /** This is an event handler for the afterExecution event of sourceCopyControl. */
    sourceCopyControlAfterExecution(event: DataSourceExecutionEvent) {
        if(event.getAction() === DataSourceAction.SEARCH) {
            const companyIds = event.dataSource.summaryData.get("company_ids")
            if(Array.isArray(companyIds)) this._companyIds = companyIds.map(obj => obj.id)
            this._hasDuplicateAfterOrderEntry = event.dataSource.summaryData.get("has_duplicate_after_order_entry")
            this._hasDuplicate = event.dataSource.summaryData.get("has_duplicate")
        }
        if(event.dataSource.summaryData.get("has_multiple_companies") == false) {
            const copyColumn = this.tableOrderDuplicateControl.getColumnByCaption("Copy to Company");
            this.tableOrderDuplicateControl.setColumnVisibility(copyColumn, false);
        }
    }

        /** This is an event handler for the onRowDisplay event of tableOrderDuplicateControl. */
    tableOrderDuplicateControlOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow: ModelRow = tableRow.data;
        const copyTb = tableRow.findComponentById("textboxCopyToCompany");
        if (modelRow.get("copy_type") !== "C") {
            copyTb.visible = false;
            copyTb.required = false;
        } else {
            copyTb.enabled = true;
            copyTb.required = true;
        }
    }

    /** This is an event handler for the afterDropdownSelection event of textboxCopyType. */
    textboxCopyTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        const targetTb = event.target as Textbox;
        if (event.selection.value === "O" && this._hasDuplicateAfterOrderEntry) {
            targetTb.selectedItem = event.deselection;
            targetTb.validationWarning = "There is already a duplicate control record for duplicate after order entry.";
            return;
        } else if (event.selection.value === "D" && this._hasDuplicate) {
            targetTb.selectedItem = event.deselection;
            targetTb.validationWarning = "There is already a duplicate control record for duplicate.";
            return;
        }

        const tableRow = TableRow.getContainingTableRow(targetTb);
        const copyTb = tableRow.findComponentById("textboxCopyToCompany") as Textbox;
        if (event.selection.value === "C") {
            copyTb.visible = true;
            copyTb.required = true;
        } else {
            this._companyIds = this._companyIds.filter(id => id !== tableRow.data.get("copy_to_company"));
            copyTb.clear();
            copyTb.visible = false;
            copyTb.required = false;
        }
    }
}
